import { FilePurpose, HTTPErrorType } from 'dy-frontend-http-repository/lib/data/enums';
import { HTTPErrorResponse } from 'dy-frontend-http-repository/lib/data/types';
import { RichTextFormat } from 'dy-frontend-shared/lib/data/valueObjects';
import { FileHashPreviewSize } from 'dy-frontend-shared/lib/data/valueObjects/FileHashPreview/enums';
import { FileUtils } from 'dy-frontend-shared/lib/utils';
import { useStore } from 'effector-react';
import React, { HTMLAttributes, useEffect, useState } from 'react';

import ConfirmationPopover from '@app/components/ConfirmationPopover';
import { $client } from '@app/containers/pages/Client/store/states';
import { $authorizedUser, $permissions } from '@app/containers/store/states';
import { fileHashPreview } from '@app/data/consts';
import { ToastUtils } from '@app/data/utils';
import { useUpload } from '@app/hooks';
import { FileInformationStatus } from '@app/hooks/useUpload/enums';
import { FileInformation } from '@app/hooks/useUpload/types';
import { getFileMaxSizeValidator } from '@app/hooks/validation/functions';
import { Button, Classes, Intent } from '@blueprintjs/core';
import FileTilePreview from '@components/FileTilePreview';
import FileUploadButton from '@components/FileUploadButton';
import Flex from '@components/Flex';
import RichEditor from '@components/RichEditor';
import richEditorPlugins from '@components/RichEditor/plugins';
import { AdditionalControl, AttachedFile } from '@components/RichEditor/types';

import { amountOfNotesOnPage } from '../../consts';
import { clientNotesApi } from '../../store/apis';
import { createClientNote, fetchClientNote, fetchClientNotes, updateClientNote } from '../../store/effects';
import { setEditingClientNote } from '../../store/events';
import { $clientNotes, $editingClientNote } from '../../store/states';

export type Props = HTMLAttributes<HTMLDivElement>;

const clientNoteFileValidators = [getFileMaxSizeValidator({ maxSize: 125000000 })];

const ClientNoteEditor: React.FC<Props> = ({ ...props }) => {
    const client = useStore($client);
    const editingClientNote = useStore($editingClientNote);
    const clientNotes = useStore($clientNotes);
    const me = useStore($authorizedUser);

    const [isUpserting, setIsUpserting] = useState(false);
    const [contentForcedValue, setContentForcedValue] = useState<string | null>(null);
    const [content, setContent] = useState('');
    const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);

    const {
        files: clientNoteFiles,
        upload: handleUploadClientNoteFiles,
        isUploading,
    } = useUpload({
        clearFilesOnUploadSuccess: true,
        validators: clientNoteFileValidators,
        onUploadSuccess: (files) => {
            setAttachedFiles((prevFiles) => [...getRichEditorAttachedFiles(files), ...prevFiles]);
        },
    });

    const isTaskMessageContentEmpty =
        new RichTextFormat(content, richEditorPlugins).getContentAsPlainText().trim().length === 0;

    useEffect(() => {
        if (editingClientNote) {
            // Get task message attached files
            const editingTaskMessageAttachedFiles: AttachedFile[] = [];
            for (let i = 0; i < editingClientNote.files.length; i++) {
                const attachment = editingClientNote.files[i];

                let previewUrl: string | undefined;
                if (attachment.file.preview_path) {
                    previewUrl = fileHashPreview.getPreviewUrl(attachment.file.preview_path, FileHashPreviewSize.SM);
                }

                editingTaskMessageAttachedFiles.push({
                    id: attachment.file.id,
                    key: `${attachment.index}`,
                    progress: 1,
                    loading: false,
                    extension: attachment.file.extension,
                    name: attachment.file.original_name,
                    size: attachment.file.size,
                    src: previewUrl,
                });
            }

            setAttachedFiles(editingTaskMessageAttachedFiles);
            setContentForcedValue(editingClientNote.content);
        }
    }, [editingClientNote]);

    if (!client || !me) {
        return null;
    }

    const handleUpdate = async () => {
        if (isTaskMessageContentEmpty) {
            return;
        }

        if (!editingClientNote) {
            return;
        }

        setIsUpserting(true);

        try {
            // Get valid file IDs array
            const fileIds = attachedFiles.map((file) => (file.id ? file.id : null)).filter((id) => id !== null) as ID[];

            // Create task message
            const clientNoteRef = await updateClientNote({
                id: editingClientNote?.id,
                input: { content: content, file_ids: fileIds },
            });

            // Fetch task message
            const clientNote = await fetchClientNote({ clientNoteId: clientNoteRef.id });
            clientNotesApi.update({ clientNote });

            setContent('');
            setAttachedFiles([]);

            setEditingClientNote(null);
        } catch (e) {
            // Log
            console.error(e);

            const response = (e as any).response as HTTPErrorResponse;
            if (response.data.type === HTTPErrorType.MISSING) {
                // Show error message
                ToastUtils.showToast({
                    message: `Task note for client with ID of ${client.id} were not found`,
                    intent: Intent.DANGER,
                });
            }
        } finally {
            setIsUpserting(false);
        }
    };

    const handleCreate = async (isInternal: boolean) => {
        if (isTaskMessageContentEmpty) {
            return;
        }

        setIsUpserting(true);

        try {
            // Get valid file IDs array
            const fileIds = attachedFiles.map((file) => (file.id ? file.id : null)).filter((id) => id !== null) as ID[];

            // Create task message
            await createClientNote({ user_id: client.id, content, file_ids: fileIds });

            // Fetch task messages
            const fetchedClientNotes = await fetchClientNotes({
                pagination: {
                    _cursor: {
                        direction: 'asc',
                        limit: amountOfNotesOnPage,
                        start: clientNotes && clientNotes.items.length > 0 ? clientNotes.items[0].id : undefined,
                    },
                },
                filter: { user_id: client.id, is_archived: '0' },
            });

            clientNotesApi.addAtStartAfterSuccessfulCreation({ clientNotes: fetchedClientNotes });
            setContent('');
            setAttachedFiles([]);
        } catch (e) {
            // TODO: handle error
            console.error(e);
        } finally {
            setIsUpserting(false);
        }
    };

    const getRichEditorAttachedFiles = (files: FileInformation[]): AttachedFile[] => {
        if (files.length === 0) {
            return [];
        }

        return files.map((file) => {
            let previewUrl: string | undefined;
            if (file.verifiedFileResource?.preview_path) {
                previewUrl = fileHashPreview.getPreviewUrl(
                    file.verifiedFileResource.preview_path,
                    FileHashPreviewSize.SM
                );
            }

            return {
                id: file.verifiedFileResource?.id,
                key: file.key,
                loading: file.status === FileInformationStatus.UPLOADING,
                progress: file.progress,
                name: file.file.name,
                size: file.file.size,
                extension: FileUtils.getFileExtension(file.file.name),
                src: previewUrl,
            };
        });
    };

    const richEditorUploadingAttachedFiles = getRichEditorAttachedFiles(clientNoteFiles);

    const renderFileAttachmentButton = () => {
        return (
            <FileUploadButton
                minimal
                multiple
                icon="paperclip"
                className="ml-1"
                onAttachFiles={(files) => {
                    handleUploadClientNoteFiles(FilePurpose.USER_NOTE_ATTACHMENT, files);
                }}
            >
                File
            </FileUploadButton>
        );
    };

    const getAdditionalActions = (): AdditionalControl[] => {
        const additionalActions: AdditionalControl[] = [];

        // Add file
        additionalActions.push({ alignment: 'right', Component: renderFileAttachmentButton() });

        return additionalActions;
    };

    const renderRemoveFileAction = (fileKey: string) => {
        const handleRemoveFile = () => {
            setAttachedFiles((prevTaskMessageAttachedFiles) =>
                prevTaskMessageAttachedFiles.filter(
                    (taskMessageAttachedFile) => taskMessageAttachedFile.key !== fileKey
                )
            );
        };

        return (
            <ConfirmationPopover
                title="Are you sure you want to remove file?"
                description="When confirmed, file will be removed"
                actions={[
                    <Button intent={Intent.DANGER} className={Classes.POPOVER_DISMISS} onClick={handleRemoveFile}>
                        Yes, remove file
                    </Button>,
                ]}
            >
                <Button minimal icon="trash" intent={Intent.DANGER} />
            </ConfirmationPopover>
        );
    };

    const renderFileActions = (fileKey: string) => {
        return <>{renderRemoveFileAction(fileKey)}</>;
    };

    const renderFileItem = (file: AttachedFile) => {
        return (
            <FileTilePreview
                fileName={file.name}
                fileSize={file.size}
                extension={file.extension}
                src={file.src}
                alt={file.alt}
                loading={file.loading}
                progress={file.progress}
                actions={renderFileActions(file.key)}
            />
        );
    };

    const renderCancelEditingButton = () => {
        if (!editingClientNote) {
            // Task message for editing was NOT set
            return null;
        }

        const handleCancelTaskMessage = () => {
            setEditingClientNote(null);
            setContent('');
            setAttachedFiles([]);
        };

        return (
            <Button
                className="ml-1"
                outlined
                icon="small-cross"
                intent={Intent.DANGER}
                onClick={handleCancelTaskMessage}
            >
                Cancel editing
            </Button>
        );
    };

    const renderSendTaskMessageButton = () => {
        const isButtonLoading = isUploading || isUpserting;
        const isButtonDisabled = isTaskMessageContentEmpty;

        if (editingClientNote) {
            // Can't create task message because task message for update is present
            return null;
        }

        return (
            <Button
                loading={isButtonLoading}
                disabled={isButtonDisabled}
                className="ml-1"
                icon="send-message"
                intent={Intent.PRIMARY}
                onClick={() => handleCreate(false)}
            >
                Send
            </Button>
        );
    };

    const renderUpdateButton = () => {
        const isButtonLoading = isUploading || isUpserting;
        const isButtonDisabled = isTaskMessageContentEmpty;

        if (!editingClientNote) {
            // Can't update task message because task message for update is NOT present
            return null;
        }

        return (
            <Button
                disabled={isButtonDisabled}
                loading={isButtonLoading}
                className="ml-1"
                icon="send-message"
                intent={Intent.PRIMARY}
                onClick={handleUpdate}
            >
                Update
            </Button>
        );
    };

    // Primary action renderer
    const renderPrimaryAction = () => {
        return (
            <Flex>
                {renderCancelEditingButton()}
                {renderUpdateButton()}
                {renderSendTaskMessageButton()}
            </Flex>
        );
    };

    return (
        <div {...props}>
            <RichEditor
                placeholder="Enter client note"
                value={content}
                files={[...attachedFiles, ...richEditorUploadingAttachedFiles]}
                forceValue={contentForcedValue ? contentForcedValue : null}
                additionalActions={getAdditionalActions()}
                renderFileItem={renderFileItem}
                primaryAction={renderPrimaryAction()}
                onChange={(data) => setContent(data)}
                onForceValueSet={() => setContentForcedValue(null)}
            />
        </div>
    );
};

export default ClientNoteEditor;
